/**
 * legalApi.ts — client access to the Wave 4 legal layer.
 * Public content is resolved by the server from the request host (never
 * from a client-supplied brand), and signed-in content from the user's
 * own workspace. No brand is hardcoded anywhere in this layer.
 */
import { safeFetch } from '../../lib/api';
import type { WorkspaceLegalConfig } from '../../lib/legalConfig';
import { emptyLegalConfig, normaliseLegalConfig } from '../../lib/legalConfig';

export interface PublicLegalView {
  workspaceId: string | null;
  configured: boolean;
  links: WorkspaceLegalConfig['links'];
  privacyContactEmail: string;
  postalAddress: string;
  documents: WorkspaceLegalConfig['documents'];
  disclosures: WorkspaceLegalConfig['disclosures'];
  documentSlots: WorkspaceLegalConfig['documentSlots'];
}

function toView(data: any): PublicLegalView {
  const cfg = normaliseLegalConfig(data, typeof data?.workspaceId === 'string' ? data.workspaceId : '');
  return {
    workspaceId: data?.workspaceId ?? null,
    configured: !!data?.configured,
    links: cfg.links,
    privacyContactEmail: cfg.privacyContactEmail,
    postalAddress: cfg.postalAddress,
    documents: cfg.documents,
    disclosures: cfg.disclosures,
    documentSlots: cfg.documentSlots,
  };
}

/** Public legal content for this host's workspace (no auth required). */
export async function fetchPublicLegal(): Promise<PublicLegalView> {
  try {
    const data = await safeFetch('/api/legal/public');
    return toView(data);
  } catch {
    return toView({ ...emptyLegalConfig(''), workspaceId: null, configured: false });
  }
}

/** Legal content for the signed-in user's own workspace. */
export async function fetchUserLegal(): Promise<PublicLegalView> {
  try {
    const data = await safeFetch('/api/legal/public-for-user');
    return toView(data);
  } catch {
    return toView({ ...emptyLegalConfig(''), workspaceId: null, configured: false });
  }
}

export async function recordConsent(input: {
  kind: string;
  version: number;
  applicationId?: string;
  programId?: string;
}): Promise<void> {
  await safeFetch('/api/legal/consent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}
